import PropTypes from 'prop-types';
import { forwardRef } from 'react';

import { ButtonIconArea, ButtonIconCircle, ButtonLabel } from './Button.styled';

const ButtonIcon = forwardRef(function ButtonIcon(
  { children, variant, tone, size, href, Icon, label, labelPlace, ...props },
  ref
) {
  const elAttrs = href ? { href, as: 'a' } : { type: 'button' };

  return (
    <ButtonIconArea
      ref={ref}
      $size={size}
      $variant={variant}
      $tone={tone}
      {...elAttrs}
      {...props}
      aria-label={props['aria-label'] || label}
    >
      {labelPlace === 'before' && <ButtonLabel>{label}</ButtonLabel>}
      <ButtonIconCircle>
        <Icon width="20" />
      </ButtonIconCircle>
      {labelPlace === 'after' && <ButtonLabel>{label}</ButtonLabel>}
    </ButtonIconArea>
  );
});

export default ButtonIcon;

ButtonIcon.defaultProps = {
  variant: 'outline',
  tone: 'primary',
  size: 'md',
};

ButtonIcon.propTypes = {
  Icon: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
  /** Defines the button overall layout */
  variant: PropTypes.oneOf([
    /** the button with border */
    'outline',
    /** the button without bg or border */
    'ghost',
  ]),
  /** Defines the button color scheme */
  tone: PropTypes.oneOf(['primary', 'secondary', 'destructive', 'subtle']),
  /** Defines the button padding and font-size */
  size: PropTypes.oneOf(['sm', 'md']),
  /** When provided it renders an anchor instead of button element */
  href: PropTypes.string,
  /** The button accessible name */
  label: function validateLabel(props) {
    const isEmpty = (str) => !str || !str.trim();
    if (isEmpty(props.label) && isEmpty(props['aria-label']) && isEmpty(props['aria-labelledby'])) {
      return new Error(
        `The ButtonIcon must have an accessible name. Use label, aria-label or aria-labelledBy.`
      );
    }
    return null;
  },
  /** If set, it will display the label */
  labelPlace: PropTypes.oneOf(['before', 'after']),
};
